import cn from 'classnames/bind';
import { equals } from 'ramda';
import React, {
    FC,
    PropsWithChildren,
    useEffect,
    useRef,
    useCallback,
    useState,
    useMemo,
} from 'react';
import { usePopper } from 'react-popper';
import { CSSTransition } from 'react-transition-group';

import { useRightClick } from '@/scenarios/legacy-player/components/PlayerSkin/hooks/useRightClick';

import { SeamlessResizeController } from '../SeamlessResizeController';

import { config } from './Popup.config';
import { PopupProps } from './Popup.types';
import { PopupContext } from './PopupContext';
import styles from './Popup.module.css';

const cx = cn.bind(styles);
const animationClassNames = {
    enter: cx('animation-enter'),
    enterActive: cx('animation-enter-active'),
    exit: cx('animation-exit'),
    exitActive: cx('animation-exit-active'),
    exitDone: cx('animation-exit-done'),
};

const Popup: FC<PropsWithChildren<PopupProps>> = (props) => {
    const {
        children,
        targetRef,
        placement,
        targetHovered,
        lockTargetHover,
        visible,
        hideDelay = config.defaultHideDelay,
        enableSeamlessResize = false,
        keepMounted = false,
        onPopupClose,
    } = props;
    const containerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const hideTimerRef = useRef<number>();
    const [visibleOnScreen, setVisibleOnScreen] = useState(false);

    const {
        styles: popperStyles,
        attributes: popperAttributes,
        update,
    } = usePopper(targetRef.current, popupRef.current, {
        placement,
        modifiers: [
            {
                name: 'preventOverflow',
                options: {
                    padding: 32,
                },
            },
        ],
    });

    const handleEnter = useCallback(() => {
        if (update) {
            update();
        }

        setVisibleOnScreen(true);
    }, [update]);

    const handleExited = useCallback(() => {
        setVisibleOnScreen(false);
        onPopupClose?.();
    }, [onPopupClose]);

    useEffect(() => {
        if (targetHovered) {
            lockTargetHover(true);
        } else {
            hideTimerRef.current = window.setTimeout(() => {
                lockTargetHover(false);
            }, hideDelay);

            return () => {
                clearTimeout(hideTimerRef.current);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetHovered, hideDelay]);

    const inner = (
        <div ref={containerRef} className={cx('container')}>
            <div className={cx('content')}>{children}</div>
        </div>
    );

    const popupChildren = enableSeamlessResize ? (
        <SeamlessResizeController visible={visible} containerRef={containerRef}>
            {inner}
        </SeamlessResizeController>
    ) : (
        inner
    );

    const popupContextValue = useMemo(
        () => ({
            visible: visibleOnScreen,
        }),
        [visibleOnScreen]
    );

    const preventContextMenu = useCallback((e?: MouseEvent) => {
        if (!e) return;
        e.preventDefault();
        e.stopPropagation();
    }, []);
    useRightClick(popupRef, preventContextMenu);

    return (
        <div
            className={cx('root', {
                root_visible: visible,
            })}
            ref={popupRef}
            style={popperStyles.popper}
            {...popperAttributes.popper} // eslint-disable-line react/jsx-props-no-spreading
        >
            <PopupContext.Provider value={popupContextValue}>
                <CSSTransition
                    in={visible}
                    classNames={animationClassNames}
                    timeout={config.animationDuration}
                    onEnter={handleEnter}
                    onExited={handleExited}
                    mountOnEnter
                    unmountOnExit={!keepMounted}
                >
                    {popupChildren}
                </CSSTransition>
            </PopupContext.Provider>
        </div>
    );
};

export default React.memo<FC<PropsWithChildren<PopupProps>>>(Popup, equals);
